// The paper trail: what the pad proposed, and what the market did next.
//
// A placed bet is settled because somebody backed it. Everything ELSE the app
// printed — every set dealt, the live pad saved before the prices moved,
// Today's picks — is settled here the next morning from the same closing
// prices and results, whether or not a cent went on it. A pad that only scored
// the tickets someone liked the look of would be scoring the punter, not the
// strategy, and the strategy is the thing that has to earn its place.
//
// Pure: no storage, no network, no clock of its own. `stores/settlement.js`
// hands it a day's closing rows and results and writes the answers away;
// `stores/paper-trail.js` reads them back out for the screens.
//
// The verdict thresholds below are the same kill switch backtests run — a
// strategy does not get to claim an edge on the pad at a sample size that
// would be refused in a backtest, which is why MIN_BETS is imported rather
// than restated.

import { resolveLeg, betOutcome, returnedFor, betClv } from './stores/settlement.js';
import { meanInterval } from './engine/metrics.js';
import { MIN_BETS } from './thresholds.js';
import { localDate } from './tickets.js';
import { legsOverdue } from './results-due.js';
import { pct, signedPct } from './format.js';

/** How far back a strategy's live evidence looks. */
export const EVIDENCE_DAYS = 30;

/** Below either of these a strategy has no evidence at all, only anecdotes. */
export const EVIDENCE_MIN_TICKETS = 30;
export const EVIDENCE_MIN_LEGS = 100;

const cents = (n) => Math.round(n * 100) / 100;

const mean = (xs) => (xs.length ? xs.reduce((a, v) => a + v, 0) / xs.length : null);

/**
 * YYYY-MM-DD plus n calendar days. String arithmetic for the same reason
 * `store.js`'s `addDay` uses it: a bare date is a calendar day, and adding 24
 * hours to one lands on the wrong day either side of a daylight-saving change.
 */
export function shiftDate(date, n = 1) {
  const [y, m, d] = String(date).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/**
 * One proposal, settled from a day's closing rows and results.
 *
 * The legs go through `resolveLeg` — the same function a placed bet's legs go
 * through — so a proposed ticket and a placed one are judged by one set of
 * opinions about which closing row to credit and what a draw means.
 *
 *   outcome   'won' | 'lost' | 'void' | 'pending'. Pending is `betOutcome`'s
 *             null said out loud, because a list of tickets on a screen needs
 *             a word for "not yet" more than it needs an absence.
 *   clv       the whole ticket's, or null when nothing on it closed.
 *   staked    what the ticket said to stake. Nobody necessarily did.
 *   returned  what that stake would have come back as — null until decided,
 *             never 0, because 0 is what a losing ticket returns.
 */
export function settleProposal(proposal, closingRows, results, nowIso = new Date().toISOString()) {
  const legs = (proposal?.legs ?? []).map((leg) => resolveLeg(leg, results, closingRows, nowIso));
  const outcome = betOutcome(legs);
  const staked = Number(proposal?.multi?.stake) || 0;
  return {
    ...proposal,
    legs,
    outcome: outcome ?? 'pending',
    clv: betClv(legs),
    staked,
    returned: outcome ? returnedFor(staked, legs) : null,
  };
}

/**
 * A day's settled proposals, counted.
 *
 * STAKED AND RETURNED COUNT DECIDED TICKETS ONLY, and so therefore does the
 * ROI. A ticket still waiting on a Sunday night game has a stake but no
 * return, and carrying it in the denominator would print a loss the pad has
 * not taken yet.
 *
 * `avgClv` is the mean over LEGS with a closing price, not over tickets: a
 * four-leg ticket with one closing price is one leg's worth of evidence about
 * whether the pad beats the market, not a quarter of a ticket's.
 *
 * `unresulted` is a SUBSET of `pending`, never a sixth outcome. Nothing here
 * is stored differently — a proposal whose score never arrived is still
 * `outcome: 'pending'` on the record — and every count that already exists
 * means exactly what it meant before, so `yesterdayLine` and `evidenceFor`
 * read the same. A caller that wants to print the split subtracts one from the
 * other; a caller that does not, ignores it.
 *
 * `now` is a parameter rather than a clock read inside, the same way
 * `evidenceFor` takes `today`: whether a result is overdue is a question about
 * the hour it is asked at, and a function that read its own clock could not be
 * tested at a fixed one.
 */
export function summariseDay(settled, { now = Date.now() } = {}) {
  const list = Array.isArray(settled) ? settled : [];
  const count = (outcome) => list.filter((p) => p?.outcome === outcome).length;
  const decided = list.filter((p) => p?.outcome && p.outcome !== 'pending');
  // Keyed off the same test `pending` uses, so the subset can never come out
  // larger than the set and a caller subtracting one from the other cannot go
  // negative.
  const unresulted = list.filter((p) => p?.outcome === 'pending' && legsOverdue(p?.legs, now)).length;

  const legs = list.flatMap((p) => p?.legs ?? []);
  // A CLOSING PRICE IS NOT THE SAME THING AS A MEASURABLE CLOSE (engine review
  // #6). Every leg here may carry the price its own book settled at; only the
  // ones with a `clvSource` were measured against the exchange or against the
  // board without that book, and only those are evidence about whether the pad
  // beat the market. A leg settled before the rule, and never re-derived, has
  // the price and not the answer.
  const withClose = legs.filter((l) => l?.clvSource && Number.isFinite(l?.clv));
  const clvs = withClose.map((l) => l.clv);
  const staked = decided.reduce((a, p) => a + (Number(p.staked) || 0), 0);
  const returned = decided.reduce((a, p) => a + (Number(p.returned) || 0), 0);

  return {
    proposed: list.length,
    decided: decided.length,
    won: count('won'),
    lost: count('lost'),
    void: count('void'),
    pending: count('pending'),
    unresulted,
    avgClv: mean(clvs),
    legsWithClose: withClose.length,
    // The spread of those legs, as the interval the verdict is actually made
    // on. Null under two legs, where there is no spread to measure.
    clvInterval: meanInterval({
      n: clvs.length,
      sum: clvs.reduce((a, v) => a + v, 0),
      sumSq: clvs.reduce((a, v) => a + v * v, 0),
    }),
    staked: cents(staked),
    returned: cents(returned),
    roi: staked > 0 ? (returned - staked) / staked : null,
  };
}

/**
 * Which of the four things the numbers are allowed to say.
 *
 *   'too-few'      not enough decided tickets, or not enough priced legs, to
 *                  say anything at all. The numbers are still printed; the
 *                  verdict is what stops them being read as a result.
 *   'early'        enough to show a number, not enough to call it.
 *   'no-edge'      a backtest's worth of legs, and the whole interval below zero.
 *   'beats-close'  a backtest's worth of legs, and the whole interval above it.
 *
 * THE COUNT IS NOT THE TEST, IT IS THE ENTRY FEE. A mean of +0.4 points over
 * three hundred legs whose interval runs from −1.9 to +2.7 is a strategy that
 * has shown nothing, and calling it "beats the close" on the sign of the mean
 * was the app making exactly the claim it tells the punter not to make. So the
 * count opens the question and the INTERVAL answers it: the lower bound has to
 * clear zero to say the pad is beating the close, the upper bound has to fall
 * below it to say the close is beating the pad, and everything in between is
 * still early however many legs it has.
 *
 * Exported because Insights asks the same question of the WHOLE pad rather than
 * of one strategy, and a second copy of these thresholds is how a screen starts
 * claiming an edge the strategy cards would refuse.
 */
export function verdictFrom(decided, legsWithClose, avgClv, interval = null) {
  if (decided < EVIDENCE_MIN_TICKETS || legsWithClose < EVIDENCE_MIN_LEGS) return 'too-few';
  if (legsWithClose >= MIN_BETS && avgClv !== null && interval) {
    if (interval.lo > 0) return 'beats-close';
    if (interval.hi < 0) return 'no-edge';
  }
  return 'early';
}

/** The verdict in the app's own words, for a card that has no room for a paragraph. */
export function verdictText(verdict) {
  if (verdict === 'beats-close') return 'Beats the close';
  if (verdict === 'no-edge') return 'No edge · the close is beating it';
  if (verdict === 'early') return 'Early days';
  return 'Too few to tell';
}

/**
 * What one strategy has actually done on the pad, over the last `days` days
 * ending today. `settledByDate` is `{ 'YYYY-MM-DD': [settled proposals] }` —
 * exactly what `stores/settlement.js` writes and `stores/paper-trail.js`
 * holds.
 */
export function evidenceFor(strategyId, settledByDate, { days = EVIDENCE_DAYS, today = localDate(Date.now()) } = {}) {
  const mine = [];
  for (let i = 0; i < days; i += 1) {
    const onDay = settledByDate?.[shiftDate(today, -i)];
    if (!Array.isArray(onDay)) continue;
    for (const p of onDay) if (p?.strategyId === strategyId) mine.push(p);
  }

  const s = summariseDay(mine);
  return {
    days,
    proposed: s.proposed,
    decided: s.decided,
    won: s.won,
    strike: s.decided ? s.won / s.decided : null,
    avgClv: s.avgClv,
    clvInterval: s.clvInterval,
    legsWithClose: s.legsWithClose,
    roi: s.roi,
    verdict: verdictFrom(s.decided, s.legsWithClose, s.avgClv, s.clvInterval),
  };
}

/**
 * How a strategy's live record compares with Classic's over the same window.
 * Classic claims nothing — a seeded random draw from the favourites pool — so
 * it is the control group: a strategy that cannot beat a random draw from the
 * same pool does not deserve a place in the app.
 *
 *   clvDelta     evidence's avgClv minus control's, in points — +0.014 is 1.4
 *                points of CLV ahead of Classic over the same window.
 *   strikeDelta  evidence's strike minus control's, the same way.
 *   comparable   true only when BOTH sides have a backtest's worth of priced
 *                legs (`EVIDENCE_MIN_LEGS`). Short of that on either side, the
 *                avgClv or strike being subtracted is too thin to mean
 *                anything, so the comparison is withheld rather than printed.
 */
export function compareEvidence(evidence, control) {
  const e = evidence ?? {};
  const c = control ?? {};
  const comparable = (e.legsWithClose ?? 0) >= EVIDENCE_MIN_LEGS && (c.legsWithClose ?? 0) >= EVIDENCE_MIN_LEGS;
  return {
    clvDelta: Number.isFinite(e.avgClv) && Number.isFinite(c.avgClv) ? e.avgClv - c.avgClv : null,
    strikeDelta: Number.isFinite(e.strike) && Number.isFinite(c.strike) ? e.strike - c.strike : null,
    comparable,
  };
}

/**
 * Yesterday's pad in one sentence — the line History opens with and the one
 * thing somebody reads before they look at anything else.
 *
 * Still-to-settle comes FIRST, before the strike rate, because a day that is
 * half-counted is not a day with a bad strike rate. And a day with no closing
 * prices says so rather than printing a CLV of nothing.
 */
export function yesterdayLine(summary) {
  if (!summary?.proposed) return 'Yesterday: nothing was proposed';
  if (summary.pending > 0) return `Yesterday's pad: ${summary.pending} of ${summary.proposed} still to settle`;

  const landed = `Yesterday's pad: ${summary.won} of ${summary.proposed} landed`;
  const avgClv = summary.avgClv;
  if (!summary.legsWithClose || avgClv === null) return `${landed} · no closing prices yet`;
  if (avgClv > 0) return `${landed} · prices beat the close by ${signedPct(avgClv)}`;
  if (avgClv < 0) return `${landed} · the close beat the prices by ${pct(-avgClv)}`;
  return `${landed} · prices level with the close`;
}
